import React from 'react';
import { motion } from 'framer-motion';
import { Plan, SubscriptionStatus } from '../../services/api';

// Extended types for new tier system
interface ExtendedSubscriptionStatus extends SubscriptionStatus {
  free_tier_eligible?: boolean;
  wallet_balance_usd?: number;
  balance_check_error?: string;
}

interface PlanSelectorProps {
  plans: Plan[];
  subscription?: ExtendedSubscriptionStatus | null;
  onSelectPlan: (plan: Plan) => void;
  currentTier?: string;
}

const CheckIcon: React.FC = () => (
  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const PlanSelector: React.FC<PlanSelectorProps> = ({
  plans,
  subscription,
  onSelectPlan,
  currentTier
}) => {
  const isFreeTierEligible = subscription?.free_tier_eligible || false;
  const walletBalance = subscription?.wallet_balance_usd || 0;
  const isStarterUser = subscription?.tier === 'starter' || currentTier === 'starter';
  const balanceCheckError = subscription?.balance_check_error;

  // Get the starter plan from the plans list
  const starterPlan = plans.find(p => p.is_lifetime || p.name?.toLowerCase().includes('starter'));

  // Features shared by both tiers
  const features = [
    'Full bot access',
    'Unlimited capital',
    'Up to 50 positions',
    'All features included',
    'Priority support',
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Free Tier Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
        className={`relative p-4 sm:p-6 rounded-xl border transition-all ${
          isFreeTierEligible
            ? 'bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-500/50'
            : 'bg-gray-900/50 border-gray-700'
        }`}
      >
        {/* Current Tier Badge */}
        {isFreeTierEligible && !isStarterUser && (
          <div className="absolute -top-2 -right-2 px-2 py-1 bg-green-600 text-xs font-semibold rounded-full">
            Active
          </div>
        )}

        <div className="mt-2">
          <h4 className="text-lg sm:text-xl font-semibold text-white">Free Tier</h4>
          <p className="text-gray-400 text-sm">Balance-based access</p>
        </div>

        <div className="mt-4">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl sm:text-3xl font-bold text-white">$500+</span>
            <span className="text-gray-400">balance</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Maintain $500+ in your wallet
          </p>
        </div>

        {/* Current Balance Display */}
        <div className="mt-4 p-3 bg-black/30 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">Your Balance</span>
            {balanceCheckError ? (
              <span className="text-sm text-yellow-400">Unable to check</span>
            ) : (
              <span className={`text-sm font-semibold ${
                walletBalance >= 500 ? 'text-green-400' : 'text-gray-400'
              }`}>
                ${walletBalance.toFixed(2)}
              </span>
            )}
          </div>
          {walletBalance > 0 && walletBalance < 500 && !balanceCheckError && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-yellow-500 to-green-500 transition-all"
                  style={{ width: `${Math.min((walletBalance / 500) * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                ${(500 - walletBalance).toFixed(2)} more to unlock
              </p>
            </div>
          )}
        </div>

        <div className={`mt-4 py-2 px-4 rounded-lg text-center font-medium ${
          isFreeTierEligible
            ? 'bg-green-600/20 text-green-400 border border-green-500/30'
            : 'bg-gray-700/50 text-gray-400'
        }`}>
          {isFreeTierEligible ? 'Eligible - Free Access' : 'Deposit to Unlock'}
        </div>

        {/* Features List */}
        <ul className="mt-4 space-y-2 text-sm text-gray-400">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <CheckIcon />
              {feature}
            </li>
          ))}
        </ul>
      </motion.div>

      {/* Starter Tier Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`relative p-4 sm:p-6 rounded-xl border transition-all ${
          isStarterUser
            ? 'bg-gradient-to-br from-purple-900/30 to-purple-800/20 border-purple-500/50'
            : 'bg-gradient-to-br from-purple-900/20 to-purple-800/10 border-purple-500/30 hover:border-purple-400 cursor-pointer'
        }`}
        onClick={() => {
          if (!isStarterUser && starterPlan) {
            onSelectPlan(starterPlan);
          }
        }}
      >
        {/* Lifetime Badge */}
        <div className="absolute -top-2 -right-2 px-2 py-1 bg-purple-600 text-xs font-semibold rounded-full">
          {isStarterUser ? 'Active' : 'Lifetime'}
        </div>

        <div className="mt-2">
          <h4 className="text-lg sm:text-xl font-semibold text-white">Starter</h4>
          <p className="text-gray-400 text-sm">One-time payment</p>
        </div>

        <div className="mt-4">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl sm:text-3xl font-bold text-white">$49</span>
            <span className="text-gray-400">USDC</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Pay once, access forever
          </p>
        </div>

        {/* Lifetime Access Highlight */}
        <div className="mt-4 p-3 bg-purple-900/20 rounded-lg border border-purple-500/20">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-purple-300">Never expires</span>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: isStarterUser ? 1 : 1.02 }}
          whileTap={{ scale: isStarterUser ? 1 : 0.98 }}
          disabled={isStarterUser}
          className={`mt-4 w-full py-2 px-4 rounded-lg font-medium transition-colors ${
            isStarterUser
              ? 'bg-purple-600/30 text-purple-300 cursor-default'
              : 'bg-purple-600 hover:bg-purple-500 text-white'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isStarterUser && starterPlan) {
              onSelectPlan(starterPlan);
            }
          }}
        >
          {isStarterUser ? 'Already Purchased' : 'Get Lifetime Access'}
        </motion.button>

        {/* Features List */}
        <ul className="mt-4 space-y-2 text-sm text-gray-400">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <CheckIcon />
              {feature}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
};

export default PlanSelector;
